/**
 * 001-catalogue-admin-screens T078-T080 (US4, FR-022, data-model §6 "Re-apply preview" and §5's
 * 409). The preview's number (`RAP-000010`) lives in the route so a refresh returns to the same
 * preview, and its by-number read names the tier, so the URL carries nothing else (unlike Warehouse's
 * stock-upload preview, `inventory/uploads/Preview.tsx`, which has no `GET` route and says so) —
 * `GET /reapply/{previewId}/rows` is a real, server-paged read (SC-004a: up to 9,000 rows browsed a
 * page at a time), so this uses the same `PagedTable` every other server-paged list here does,
 * never the whole set in one response.
 *
 * A 409 on commit (data-model §5, backend `CommitReapplyHandler`) carries `movedRows[]` when some
 * base rates changed since the preview, or an empty array when the tier's discount rules changed
 * instead (every row is affected, so there is nothing to list). Either way nothing was written and
 * the Commit button disappears for good on this preview — FR-022's hard stop is "take a fresh
 * preview", never "try committing this one again". The moved-rows list is a small, already-fetched
 * array from the error body, not a second server-paged list, so it renders through `LegacyTable`
 * the same way `inventory/uploads/Preview.tsx`'s own bounded "Rejected rows" table does.
 */
import { useState } from "react";
import { Link, useLoaderData, useNavigate, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import toast from "react-hot-toast";
import { PiArrowLeftBold as ArrowLeft } from "react-icons/pi";
import type { ColumnDef } from "@tanstack/react-table";
import { Alert, Button, Text } from "rizzui";
import { EmptyState, ErrorSurface } from "@steelyard/theme";
import LegacyTable, { HeaderCell } from "@core/components/legacy-table";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { can } from "../../lib/permissions";
import { BackLink } from "../../lib/navigation";
import { ApiError } from "../../lib/api";
import { readPaging } from "../../lib/paging";
import { isGuid } from "../../lib/paths";
import type { AccountSummary, PermissionState } from "../../types";
import {
  abandonReapply,
  commitReapply,
  getReapplyPreviewByNumber,
  getReapplyRows,
  getTier,
  type CommitReapplyResult,
  type MovedBaseRate,
  type ReapplyRow,
} from "../../lib/services/catalog";

const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });
const fmt = (value: number) => MONEY.format(value);
const SIGNED = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", signDisplay: "exceptZero" });

// Headers say what each figure means to a reader, in the order it is read: the base price the new
// figure is worked out from, what customers pay now, what they will pay once this is committed, and
// the change. Accessors make each one sortable (within the page, which the footer says); money is
// right-aligned through `meta`, so a header sits over its figures.
const columns: ColumnDef<ReapplyRow>[] = [
  {
    id: "sku",
    accessorKey: "skuCode",
    header: "SKU",
    // §9.3: SKU and unit as a person reads them; the row id only if the price was since deleted.
    cell: ({ row }) =>
      row.original.skuCode ? (
        <span>
          <span className="font-mono text-xs">{row.original.skuCode}</span> {row.original.skuName}
        </span>
      ) : (
        <span className="font-mono text-xs">{row.original.tierPriceId}</span>
      ),
  },
  { id: "unit", accessorKey: "unitCode", header: "Unit", cell: ({ row }) => <span className="font-mono text-xs">{row.original.unitCode ?? "—"}</span> },
  { id: "observedBaseRate", accessorKey: "observedBaseRate", header: "Base price used", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{fmt(row.original.observedBaseRate)}</span> },
  { id: "currentRate", accessorKey: "currentRate", header: "Customers pay now", meta: { align: "right" }, cell: ({ row }) => <span className="tabular-nums">{fmt(row.original.currentRate)}</span> },
  { id: "proposedRate", accessorKey: "proposedRate", header: "After commit", meta: { align: "right" }, cell: ({ row }) => <span className="font-semibold tabular-nums">{fmt(row.original.proposedRate)}</span> },
  {
    id: "difference",
    accessorKey: "difference",
    header: "Change",
    meta: { align: "right" },
    // The API's own difference; a zero is said in words, so a row that only leaves the stale list
    // is not read as a price move.
    cell: ({ row }) =>
      row.original.difference === 0 ? (
        <span className="text-gray-500">No change</span>
      ) : (
        <span className="font-semibold tabular-nums">{SIGNED.format(row.original.difference)}</span>
      ),
  },
];

const movedColumns = [
  {
    title: "SKU",
    dataIndex: "skuId",
    key: "skuId",
    render: (value: MovedBaseRate["skuId"], record: Partial<MovedBaseRate>) =>
      record.skuCode ? (
        <span>
          <span className="font-mono text-xs">{record.skuCode}</span> {record.skuName}
        </span>
      ) : (
        <span className="font-mono text-xs">{value}</span>
      ),
  },
  {
    title: "Unit",
    dataIndex: "unitId",
    key: "unitId",
    render: (value: MovedBaseRate["unitId"], record: Partial<MovedBaseRate>) => <span className="font-mono text-xs">{record.unitCode ?? value}</span>,
  },
  {
    title: <HeaderCell title="Base rate in this preview" align="right" />,
    dataIndex: "observedBaseRate",
    key: "observedBaseRate",
    render: (value: MovedBaseRate["observedBaseRate"]) => <span className="block text-right tabular-nums">{fmt(value)}</span>,
  },
  {
    title: <HeaderCell title="Base rate now" align="right" />,
    dataIndex: "currentBaseRate",
    key: "currentBaseRate",
    render: (value: MovedBaseRate["currentBaseRate"]) => <span className="block text-right tabular-nums">{value === null ? "deleted" : fmt(value)}</span>,
  },
];

export async function reapplyPreviewLoader({ params, request }: LoaderFunctionArgs) {
  const key = params.previewNumber!;
  const searchParams = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(searchParams);
  // A preview has no by-id read to redirect from, so an id URL from before preview numbers still
  // opens in place: its rows by id, its tier from the `?tierId=` those URLs carried. Previews are
  // short-lived; nothing links to one but the Staleness screen that just made it.
  const preview = isGuid(key) ? null : await getReapplyPreviewByNumber(key);
  const previewId = preview?.previewId ?? key;
  const tierId = preview?.tierId ?? searchParams.get("tierId");
  // The tier's name, so the page says whose prices these are. A failed read only loses the name.
  const [rows, tier] = await Promise.all([
    getReapplyRows(previewId, { page, pageSize }),
    tierId ? getTier(tierId).catch(() => null) : Promise.resolve(null),
  ]);
  return { previewId, tierId, rows, tierName: tier?.name ?? null };
}

export default function ReapplyPreviewPage() {
  const { previewId, tierId, rows, tierName: loadedTierName } = useLoaderData() as Awaited<ReturnType<typeof reapplyPreviewLoader>>;
  const tierName = loadedTierName ?? "this tier";
  const navigate = useNavigate();
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();

  const [busy, setBusy] = useState<"commit" | "abandon" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [conflict, setConflict] = useState<{ movedRows: MovedBaseRate[]; rulesChanged: boolean } | null>(null);
  const [committed, setCommitted] = useState<CommitReapplyResult | null>(null);

  if (!can(permissionState, "pricing.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="commit a re-apply" />;
  }

  const freshPreviewHref = tierId ? `/reports/staleness?tierId=${tierId}` : "/reports/staleness";

  async function onCommit() {
    setBusy("commit");
    setError(null);
    setConflict(null);
    try {
      const result = await commitReapply(previewId);
      setCommitted(result);
      toast.success(`${tierName}: ${result.rowsUpdated} price(s) updated`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const moved = Array.isArray(err.raw?.movedRows) ? (err.raw!.movedRows as MovedBaseRate[]) : [];
        setConflict({ movedRows: moved, rulesChanged: moved.length === 0 });
        toast.error(err.title ?? "This preview no longer matches the catalogue");
      } else {
        setError(err);
      }
    } finally {
      setBusy(null);
    }
  }

  async function onAbandon() {
    setBusy("abandon");
    setError(null);
    try {
      await abandonReapply(previewId);
      toast.success("Preview discarded — no prices changed");
      navigate(freshPreviewHref);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title={loadedTierName ? `Re-Apply Preview — ${loadedTierName}` : "Re-Apply Preview"}
        count={rows.totalCount}
        description={`What customers on ${tierName} pay now, and what they will pay once you commit — worked out from today's base prices and ${tierName}'s discount rules.`}
        breadcrumb={[
          { name: "Home", href: "/" },
          { name: "Pricing" },
          { name: "Stale Prices", href: "/reports/staleness" },
          { name: "Re-Apply Preview" },
        ]}
      >
        <BackLink to="/reports/staleness">
          <Button as="span" variant="outline">
            <ArrowLeft aria-hidden className="me-1.5 size-4" />
            Back to stale prices
          </Button>
        </BackLink>
      </ListHeader>

      {error ? <ErrorSurface error={error} action="commit this re-apply" /> : null}

      {/* Before the decision: what each button will do, said once, beside the rows it applies to. */}
      {!committed && !conflict && rows.totalCount > 0 ? (
        <Alert color="info" bar>
          <p className="font-semibold">Nothing has changed yet</p>
          <ul className="mt-1 list-disc ps-5">
            <li>
              <b>Commit</b> sets every price below to its &ldquo;After commit&rdquo; figure straight away. Customers on {tierName} see
              the new prices from then on, and these rows leave the stale-prices list.
            </li>
            <li>Rows marked &ldquo;No change&rdquo; keep their price; committing only clears them from the stale-prices list.</li>
            <li>
              <b>Discard</b> writes nothing — the prices stay as they are and stay on the stale-prices list.
            </li>
            <li>
              If a base price or {tierName}&apos;s discount rules change before you commit, the commit is refused and you take a
              fresh preview.
            </li>
          </ul>
        </Alert>
      ) : null}

      {committed ? (
        <Alert color="success" bar>
          <p className="font-semibold">Done — {tierName}&apos;s prices are updated.</p>
          <p>
            {committed.rowsUpdated} price(s) now carry their &ldquo;After commit&rdquo; figure. Customers on {tierName} see the new
            prices from now on, and these rows are off the stale-prices list. Other tiers are unchanged.
          </p>
        </Alert>
      ) : null}

      {/* FR-022's hard stop, never a retry: no Commit button renders once a conflict is caught, in
          either arm (named moved rows, or the rules-changed arm with none to name). */}
      {conflict ? (
        <Alert color="danger" bar>
          <p className="font-semibold">
            {conflict.rulesChanged
              ? "The tier's discount rules changed since this preview was generated — every row's proposal is stale."
              : `${conflict.movedRows.length} row(s) depend on a base rate that changed since this preview was generated.`}
          </p>
          <p>No prices changed. This preview can no longer be committed — take a fresh one to see the new figures.</p>
          <div className="mt-3">
            <Link to={freshPreviewHref}>
              <Button as="span" size="sm" color="danger" variant="outline">
                Take a fresh preview
              </Button>
            </Link>
          </div>
        </Alert>
      ) : null}

      {conflict && conflict.movedRows.length > 0 ? (
        <LegacyTable
          data={conflict.movedRows}
          caption={<span className="sr-only">Rows whose base rate moved since this preview was generated</span>}
          columns={movedColumns}
          variant="modern"
          rowKey={(record, index) => `${record.skuId}-${record.unitId}-${index ?? 0}`}
        />
      ) : null}

      {!committed && !conflict ? (
        rows.totalCount === 0 ? (
          <EmptyState title="Nothing to re-apply">Every eligible price on this tier already matches what its rules would derive.</EmptyState>
        ) : (
          <>
            <PagedTable
              result={rows}
              caption={`Re-apply preview — ${rows.totalCount} row(s)`}
              columns={columns}
              getRowId={(row) => row.tierPriceId}
              // A row whose price will actually move is marked down its leading edge, so it stands
              // out from the rows that only leave the stale list. `!`: the table's own border wins otherwise.
              rowClassName={(row) => (row.difference !== 0 ? "border-l-4 !border-l-primary" : undefined)}
              empty={<EmptyState title="No rows on this page">Nothing on this page — use the pager to see the rest.</EmptyState>}
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={onCommit} isLoading={busy === "commit"} disabled={busy !== null}>
                {busy === "commit" ? "Committing…" : "Commit new prices"}
              </Button>
              <Button onClick={onAbandon} isLoading={busy === "abandon"} disabled={busy !== null} variant="outline">
                {busy === "abandon" ? "Discarding…" : "Discard"}
              </Button>
              <Text className="text-sm text-gray-500">
                Applies to the {rows.totalCount} price(s) above, on {tierName} only.
              </Text>
            </div>
          </>
        )
      ) : null}

      {committed ? (
        <Text className="text-sm text-muted-foreground">
          <BackLink to="/reports/staleness" className="underline">
            Back to stale prices
          </BackLink>
        </Text>
      ) : null}
    </div>
  );
}
